import MoneyInput from 'components/MoneyInput';
import React from 'react';
import {
  BooleanInput,
  NumberInput,
  useGetOne,
  useResourceContext,
  useRecordContext,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  calculatedFieldPath,
  isFieldVisible,
  shouldShowMarkupsForTaxClassification,
  sources,
  type RateSettingsField,
} from 'resources/rate_settings/utils/fieldsVisibility';
import { getInherited } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import { titleize } from 'inflection';
import FacilityAndProfessionalRateInput from '../position-rate-settings-form/LocationAndProfessionalRateInput';
import Heading from 'components/Heading';
import useCheckAccess from 'hooks/useCheckAccess';
import { useRateCalculations } from '../position-rate-settings-form/hooks/useRateCalculations';
import { PARENT_SETTINGS_FIELD } from '../position-rate-settings-form/constants';
import { MarkupsInputs } from './MarkupsInputs';

type ChildrenRateSettingsProps = {
  shouldShowField: (field: RateSettingsField) => boolean;
  getValues: (path: string) => unknown;
};

type RateSettingsProps = {
  source: string;
  taxClassification: 'w2.local' | 'w2.travel' | 'ten99';
  inheritedSettings: any;
  parentSettings?: any;
};

type ScalingMarginsProps = Pick<
  RateSettingsProps,
  'source' | 'taxClassification' | 'inheritedSettings' | 'parentSettings'
>;

const RateSettingsSpecificFields = ({
  source,
  shouldShowField,
  getValues,
  taxClassification,
  inheritedSettings,
  parentSettings,
}: RateSettingsProps & ChildrenRateSettingsProps) => {
  const fields = [];

  sources[taxClassification].forEach((fieldSource) => {
    if (shouldShowField(fieldSource as RateSettingsField)) {
      // TODO: improve
      if (
        [
          // rates
          'base_charge_rate_cents',
          'charge_rate_cents',
          // margins (base + computed, per classification)
          'base_margin_percent',
          'margin_percent_1099',
          'base_margin_percent_w2',
          'total_margin_percent_w2',
          'base_margin_percent_w2_tcv',
          'margin_percent_w2_tcv',
          'base_margin_percent_w2_travel',
          'margin_percent_w2_travel',
          // markups
          'invoicing_markup',
          'misc_markup',
          'operational_markup',
        ].includes(fieldSource)
      ) {
        return;
      }

      const FieldComponent = fieldSource.includes('cents') ? MoneyInput : NumberInput;
      fields.push(
        <FieldComponent
          key={`${source}.${taxClassification}.${fieldSource}`}
          source={calculatedFieldPath(`${source}.${taxClassification}`, fieldSource, getValues)}
          label={titleize(fieldSource)}
          helperText={getInherited(
            inheritedSettings,
            `${taxClassification}.${fieldSource}`,
            parentSettings,
          )}
          blankIfNull
        />,
      );
    }
  });

  return fields;
};

const ScalingMarginsInputs = ({
  source,
  taxClassification,
  inheritedSettings,
  parentSettings,
}: ScalingMarginsProps) => {
  const fields = [];
  const key = taxClassification.includes('w2') ? 'w2' : '1099';
  const settings_subhash_key = taxClassification.includes('w2') ? 'w2' : 'ten99';
  const resource = useResourceContext();
  const record = useRecordContext();
  const checkAccess = useCheckAccess();
  const action = record?.id ? 'update' : 'create';
  const isMPRS = resource === 'MarketPositionRateSetting';
  const isLPRS = resource === 'LocationPositionRateSetting';
  const isSPRS = resource === 'StatePositionRateSetting';

  if (taxClassification === 'w2.travel' || (!isMPRS && !isLPRS && !isSPRS)) {
    return null;
  }

  const baseMarginPercentGuardrailField = `base_margin_percent_${key}_guardrail`;
  const payoutRatePercentGuardrailField = `payout_rate_percent_${key}_guardrail`;
  const incrementalMarginPercentField = `incremental_margin_percent_${key}`;
  const sourcePrefix = record?.id && source ? source : 'settings';
  const appliedSource = `${sourcePrefix}.${taxClassification}`;

  const { validations } = useRateCalculations({ source: appliedSource, inheritedSettings });

  if (checkAccess(action, resource, baseMarginPercentGuardrailField) && !isLPRS) {
    fields.push(
      <NumberInput
        key={baseMarginPercentGuardrailField}
        source={`${source}.${settings_subhash_key}.${baseMarginPercentGuardrailField}`}
        label={`Base Margin Percent ${key.toUpperCase()} Guardrail`}
        helperText={getInherited(
          inheritedSettings,
          `${settings_subhash_key}.${baseMarginPercentGuardrailField}`,
          parentSettings,
        )}
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue) && validations?.validateMarginPercentGuardrail) {
            validations.validateMarginPercentGuardrail(newValue);
          }
        }}
      />,
    );
  }

  if (checkAccess(action, resource, payoutRatePercentGuardrailField) && !isLPRS) {
    fields.push(
      <NumberInput
        key={payoutRatePercentGuardrailField}
        source={`${source}.${settings_subhash_key}.${payoutRatePercentGuardrailField}`}
        label={`Payout Rate Percent ${key.toUpperCase()} Guardrail`}
        helperText={getInherited(
          inheritedSettings,
          `${settings_subhash_key}.${payoutRatePercentGuardrailField}`,
          parentSettings,
        )}
      />,
    );
  }

  if (checkAccess(action, resource, incrementalMarginPercentField)) {
    fields.push(
      <NumberInput
        key={incrementalMarginPercentField}
        source={`${source}.${settings_subhash_key}.${incrementalMarginPercentField}`}
        label={`Incremental Margin Percent ${key.toUpperCase()}`}
        helperText={getInherited(
          inheritedSettings,
          `${settings_subhash_key}.${incrementalMarginPercentField}`,
          parentSettings,
        )}
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue) && validations?.validateIncrementalMargin) {
            validations.validateIncrementalMargin(newValue);
          }
        }}
      />,
    );
  }

  return fields;
};

export const RateSettingsTaxClassificationInputs = ({
  source,
  inheritedSettings,
  taxClassification,
  showTitle = false,
}) => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const { getValues } = useFormContext();
  const locationId = useWatch({ name: 'location_id' });
  const { data: selectedLocation } = useGetOne(
    'Location',
    { id: Number(locationId) },
    { enabled: Boolean(locationId) && resource === 'LocationPositionRateSetting' },
  );
  const parentSettings = getValues(PARENT_SETTINGS_FIELD);
  const effectiveInheritedSettings = inheritedSettings ?? parentSettings ?? {};
  const billingPlan = record?.location?.primary_billing_group?.plan || record?.bgPlan;
  const isW2Travel = taxClassification === 'w2.travel';
  const locationTypeId = record?.location?.location_type_id ?? selectedLocation?.location_type_id;
  const isHospital = locationTypeId === 8;
  const shouldShowField = (field: RateSettingsField): boolean => {
    return isFieldVisible(field, resource);
  };
  const shouldShowMarkups = shouldShowMarkupsForTaxClassification({
    resource,
    taxClassification,
    billingPlan,
    shouldShowInvoicingMarkup: shouldShowField('invoicing_markup'),
    hasRecord: !!record,
  });

  return (
    <>
      {shouldShowField('charge_rate_8hr') && (
        <FacilityAndProfessionalRateInput
          source={`${source}.${taxClassification}`}
          inheritedSettings={effectiveInheritedSettings}
          taxClassification={taxClassification}
          isHospital={isHospital}
        />
      )}
      {shouldShowMarkups && (
        <MarkupsInputs
          source={`${source}.${taxClassification}`}
          inheritedSettings={effectiveInheritedSettings}
          parentSettings={parentSettings}
          taxClassification={taxClassification}
        />
      )}
      {RateSettingsSpecificFields({
        shouldShowField,
        getValues,
        source,
        taxClassification,
        inheritedSettings,
        parentSettings,
      })}

      {ScalingMarginsInputs({
        source,
        taxClassification,
        inheritedSettings,
        parentSettings,
      })}

      {!isW2Travel && (
        <>
          {showTitle && <Heading>Overtime Settings</Heading>}
          {shouldShowField('payout_max_weekly_regular_hours') && (
            <NumberInput
              source={`${source}.${taxClassification}.payout_max_weekly_regular_hours`}
              label="Payout Max Weekly Regular Hours"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.payout_max_weekly_regular_hours`,
                parentSettings,
              )}
            />
          )}

          {shouldShowField('payout_seventh_day_rule_enabled') && (
            <BooleanInput
              source={`${source}.${taxClassification}.payout_seventh_day_rule_enabled`}
              label="Payout Seventh Day Rule Enabled"
              defaultValue={null}
              format={(value) => !!value}
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.payout_seventh_day_rule_enabled`,
                parentSettings,
              )}
            />
          )}

          {shouldShowField('charge_max_weekly_regular_hours') && (
            <NumberInput
              source={`${source}.${taxClassification}.charge_max_weekly_regular_hours`}
              label="Charge Max Weekly Regular Hours"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.charge_max_weekly_regular_hours`,
                parentSettings,
              )}
            />
          )}

          {shouldShowField('charge_seventh_day_rule_enabled') && (
            <BooleanInput
              source={`${source}.${taxClassification}.charge_seventh_day_rule_enabled`}
              label="Charge Seventh Day Rule Enabled"
              defaultValue={null}
              format={(value) => !!value}
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.charge_seventh_day_rule_enabled`,
                parentSettings,
              )}
            />
          )}
        </>
      )}
    </>
  );
};
